import _ from 'lodash';
import {
  getNetworkBrandingName,
  isClip,
  isMovie,
  NEWS_GENRE,
  PAGE_TYPE_ARTICLE,
  PAGE_TYPE_CHARACTERS,
  PAGE_TYPE_CLIPS,
  PAGE_TYPE_CREDITS,
  TAB_PRIMETIME,
} from 'helpers/ContentHelper';
import {
  helperGetHomeUrl,
  helperGetLiveUrl,
  helperGetNewsUrl,
  helperGetPersonalitiesUrl,
  helperGetSportsUrl,
} from 'helpers/RouteHelper';
import {
  generateItem,
  getArticleData,
  getCharactersData,
  getCharactersDetailData,
  getClipsData,
  getCreditsData,
  getCreditsDetailData,
  getMovieData,
  getPrimetimeData,
  getSeasonData,
  getSeriesArticleData,
  getShowData,
  getShowsData,
  getShowsFilterData,
  getWatchData,
} from './BreadcrumbsHelper';

const {
  personalitiesPage_header: personalitiesLabel,
  badge_liveText: liveLabel,
  newsPage_header: newsLabel,
  sportsPage_header: sportsLabel,
} = window.Language;

const BRANDING_NAME = getNetworkBrandingName();

const HOME_ITEM = generateItem(helperGetHomeUrl(), BRANDING_NAME);
const LIVE_ITEM = videoId => generateItem(helperGetLiveUrl({ videoId }), liveLabel);
const FOX_LIVE_NAME = `Schedule`;
const FOX_LIVE_ITEM = videoId => generateItem(helperGetLiveUrl({ videoId }), FOX_LIVE_NAME);
const PERSONALITIES_ITEM = generateItem(helperGetPersonalitiesUrl(), personalitiesLabel);
const NEWS_ITEM = generateItem(helperGetNewsUrl(), newsLabel);
const SPORTS_ITEM = generateItem(helperGetSportsUrl(), sportsLabel);

export const articleDetailParser = (articleData, params) => {
  if (!articleData) return null;
  const data = [HOME_ITEM];
  data.push(getArticleData(articleData, params));
  return data;
};

export const collectionParser = route => {
  const { path } = route;
  if (path.includes('local')) {
    return [HOME_ITEM, generateItem(`/local/`, 'Local')];
  }
  return [HOME_ITEM, generateItem(`/${path}/`, _.capitalize(path))];
};

export const showsParser = ({ tab, params, selectedFilter }) => {
  const data = [HOME_ITEM];
  const { collectionId, filterId } = params;
  if (collectionId) {
    data.push(getShowsData(tab, collectionId));
  }
  if (filterId) {
    data.push(getShowsFilterData(collectionId, filterId, selectedFilter));
  }

  return data;
};

export const liveParser = (selectedTab, savedVideo) => {
  const liveItem = LIVE_ITEM(_.get(savedVideo, 'id', null));
  const liveData = [HOME_ITEM, liveItem];
  if (selectedTab === TAB_PRIMETIME) {
    liveData.push(getPrimetimeData());
  }
  return liveData;
};

export const foxLiveParser = savedVideo => {
  const liveItem = FOX_LIVE_ITEM(_.get(savedVideo, 'id', null));
  const liveData = [HOME_ITEM, liveItem];
  return liveData;
};

export const seriesDetailParser = (params, seriesData, articleData, creditsData, isPersonality) => {
  if (!seriesData) return null;
  const { pageType, pageId } = params;
  const {
    id: showId,
    name: showName,
    seriesType,
    seriesDisplayType,
    genres,
    subGenres,
  } = seriesData;
  const siteScanUrl = articleData?.siteScanUrl;
  const shouldDisplayNews =
    seriesDisplayType === 'news' || _.includes(subGenres, 'News and Information');
  const shouldDisplaySports = _.some(
    genres,
    genre => genre.toLowerCase() === 'sports' || genre === 'Sports and Fitness',
  );
  const showData = getShowData({ showId, showName, isPersonality, seriesType });
  let secondLevelBreadcrumb;
  if (isPersonality) secondLevelBreadcrumb = PERSONALITIES_ITEM;
  if (shouldDisplayNews && NEWS_ITEM) secondLevelBreadcrumb = NEWS_ITEM;
  if (shouldDisplaySports && SPORTS_ITEM) secondLevelBreadcrumb = SPORTS_ITEM;
  const seriesDetailData = [HOME_ITEM, secondLevelBreadcrumb, showData];

  const shouldAddClips = pageType === PAGE_TYPE_CLIPS && showId;
  const shouldAddArticle = pageType === PAGE_TYPE_ARTICLE && articleData;
  const shouldAddSeriesArticle = siteScanUrl && siteScanUrl.split('/').pop() === pageType;
  const shouldAddCredits = pageType === PAGE_TYPE_CREDITS && showId;
  const shouldAddCharacters = pageType === PAGE_TYPE_CHARACTERS && showId;
  const shouldAddCreditsDetail = shouldAddCredits && creditsData;
  const shouldAddCharactersDetails = shouldAddCharacters && creditsData;
  const shouldAddSeason = _.includes(pageType, 'season-');

  if (shouldAddClips) {
    seriesDetailData.push(getClipsData(showId));
  }

  if (shouldAddArticle) {
    seriesDetailData.push(getArticleData(articleData, params));
  }

  if (shouldAddSeriesArticle) {
    seriesDetailData.push(getSeriesArticleData(articleData));
  }

  if (shouldAddCredits) {
    seriesDetailData.push(getCreditsData(showId));
  }

  if (shouldAddCharacters) {
    seriesDetailData.push(getCharactersData(showId));
  }

  if (shouldAddCreditsDetail) {
    seriesDetailData.push(getCreditsDetailData(showId, pageId, creditsData));
  }

  if (shouldAddCharactersDetails) {
    seriesDetailData.push(getCharactersDetailData(showId, pageId, creditsData));
  }

  if (shouldAddSeason) {
    const seasonNumber = _.replace(pageType, 'season-', '');
    seriesDetailData.push(getSeasonData(showId, seasonNumber));
  }

  return seriesDetailData;
};

export const watchParser = (video, isLocalClip, localClipHeadline) => {
  if (!video) return null;
  const {
    contentSKU,
    currentSeason,
    genres,
    id,
    name,
    seasonNumber,
    seriesDisplayType,
    seriesName: showName,
    seriesType,
    showCode: showId,
    subGenres,
    videoType,
  } = video;

  const localHeadline = localClipHeadline || name;

  const shouldDisplayNews =
    seriesDisplayType === 'news' ||
    _.includes(subGenres, NEWS_GENRE) ||
    _.some(genres, genre => genre === NEWS_GENRE) ||
    _.some(contentSKU, sku => sku === 'us.FoxNews');

  const shouldDisplaySports = _.some(
    genres,
    genre => genre.toLowerCase() === 'sports' || genre === 'Sports and Fitness',
  );
  const isMovieData = isMovie(videoType) || isMovie(seriesType);
  const isClipData = isClip(videoType) && showId;

  let secondLevelBreadcrumb;
  if (shouldDisplayNews && NEWS_ITEM) secondLevelBreadcrumb = NEWS_ITEM;
  if (shouldDisplaySports && SPORTS_ITEM) secondLevelBreadcrumb = SPORTS_ITEM;

  const showData = getShowData({ showId, showName, seriesType });
  const movieData = getMovieData(showId, showName);
  const seasonData = currentSeason === seasonNumber ? null : getSeasonData(showId, seasonNumber);
  const showTabData = isClipData ? getClipsData(showId) : seasonData;
  const watchData = getWatchData(video);

  if (isMovieData) {
    return [HOME_ITEM, movieData, watchData];
  }

  if (isLocalClip) {
    return [
      HOME_ITEM,
      generateItem(`/local/`, 'Local'),
      generateItem(`/watch/${id}`, localHeadline),
    ];
  }

  return [HOME_ITEM, secondLevelBreadcrumb, showData, showTabData, watchData];
};
